import axios from 'axios'
import Dialog from 'lib/components/Dialog/Dialog'
import AuthContext from 'lib/context/AuthContext'
import useFocusOnMount from 'lib/hooks/useFocusOnMount'
import useOnInputChange from 'lib/hooks/useOnInputChange'
import { reportError } from 'lib/util/errorHandler'
import isUniqueConstraintError from 'lib/util/isUniqueConstraintError'
import { canPublish } from 'lib/util/recordedWebinar'
import ActionDialog from 'pages/Home/admin/ActionDialog/ActionDialog'
import FormGroup from 'pages/Home/admin/FormGroup/FormGroup'
import RecordedWebinarType from 'pages/Home/RecordedWebinarType'
import Series from 'pages/Home/Series'
import SkillLevel, { getDisplayNames } from 'pages/Home/SkillLevel'
import React, { FormEvent, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import getSlug from 'speakingurl'

export interface AddRecordedWebinarDialogProps {
  initialRecordedWebinar?: RecordedWebinarType
  series: Series[]
  onRequestClose: ()=> void
}

type EditableRecordedWebinarType = Pick<
RecordedWebinarType,
'seriesId' | 'title' | 'description' | 'rise' | 'storyline' | 'more' | 'published' | 'slug'
> & { skillLevel: string, autoGenerateUrl: boolean }

function buildInitialState(initialRecordedWebinar?: RecordedWebinarType): EditableRecordedWebinarType {
  if (initialRecordedWebinar) {
    return {
      seriesId: initialRecordedWebinar.seriesId,
      skillLevel: initialRecordedWebinar.skillLevel || '',
      title: initialRecordedWebinar.title,
      description: initialRecordedWebinar.description,
      rise: initialRecordedWebinar.rise,
      storyline: initialRecordedWebinar.storyline,
      more: initialRecordedWebinar.more,
      published: initialRecordedWebinar.published,
      slug: initialRecordedWebinar.slug,
      autoGenerateUrl: getSlug(initialRecordedWebinar.title) === initialRecordedWebinar.slug
    }
  }

  return {
    seriesId: '',
    skillLevel: '',
    title: '',
    description: '',
    rise: false,
    storyline: false,
    more: false,
    published: false,
    slug: '',
    autoGenerateUrl: true
  }
}

export default function AddRecordedWebinarDialog(props: AddRecordedWebinarDialogProps) {
  const { series: seriesList, initialRecordedWebinar, onRequestClose } = props
  const [t] = useTranslation()
  const [apiState, setApiState] = useState({ error: null, saving: false })
  const { getAccessToken } = useContext(AuthContext)
  const [state, setState] = useState(buildInitialState(initialRecordedWebinar))
  const { description, seriesId, title, rise, storyline, more, published } = state
  const onInputChange = useOnInputChange<EditableRecordedWebinarType>(setState)
  const inputEl = useRef(null)
  useFocusOnMount(inputEl)

  const generatedUrl = state.title ? getSlug(state.title) : ''
  const slug = state.autoGenerateUrl ? generatedUrl : state.slug
  const showUrlGenerationWarning =
    initialRecordedWebinar &&
    state.autoGenerateUrl &&
    state.title !== initialRecordedWebinar.title &&
    slug !== initialRecordedWebinar.slug

  const validationError = (() => {
    const categoryMissing = !rise && !storyline && !more
    if (!title || !seriesId || !description.trim()) {
      return t('home.recordedWebinarsSection.admin.addDialog.validationError')
    }
    if (categoryMissing) {
      return t('home.recordedWebinarsSection.admin.addDialog.categoryMissing')
    }
    if (published && !canPublish(initialRecordedWebinar)) {
      return t('home.recordedWebinarsSection.admin.addDialog.missingVideo')
    }
  })()

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (validationError) {
      return
    }
    setApiState({ saving: true, error: null })
    try {
      const data = {
        ...state,
        slug: getSlug(slug),
        skillLevel: state.skillLevel || null,
        ...(!initialRecordedWebinar && { detailDescription: state.description })
      }
      const accessToken = await getAccessToken()
      const headers = { Authorization: `Bearer ${accessToken}` }
      if (initialRecordedWebinar) {
        await axios.put(
          `${process.env.THREESIXTY_TRAINING_URI}/admin/recorded-webinars/${initialRecordedWebinar.id}`,
          data,
          { headers }
        )
      } else {
        await axios.post(`${process.env.THREESIXTY_TRAINING_URI}/admin/recorded-webinars`, data, { headers })
      }
      window.location.reload()
    } catch (err) {
      if (isUniqueConstraintError(err)) {
        return setApiState({ saving: false, error: t('home.recordedWebinarsSection.admin.addDialog.uniqueError') })
      }
      setApiState({ saving: false, error: t('home.recordedWebinarsSection.admin.addDialog.apiError') })
      reportError(err, `Error ${initialRecordedWebinar ? 'editing' : 'adding'} recorded webinar`, {
        recordedWebinarId: initialRecordedWebinar ? initialRecordedWebinar.id : null
      })
    }
  }

  function renderSeries() {
    const value = state['seriesId']

    return (
      <div>
        <FormGroup.Select ref={inputEl} name="seriesId" value={value} onChange={onInputChange}>
          <option value="">{t('home.recordedWebinarsSection.admin.addDialog.noSeries')}</option>
          {seriesList.map(series => (
            <option key={series.id} value={series.id}>
              {series.title}
            </option>
          ))}
        </FormGroup.Select>
      </div>
    )
  }

  function renderSkillLevel() {
    const displayNames = getDisplayNames(t)
    const value = state['skillLevel']

    return (
      <div>
        <FormGroup.Select name="skillLevel" value={value} onChange={onInputChange}>
          <option value="">{t('home.recordedWebinarsSection.admin.addDialog.noSkillLevel')}</option>
          <option value={SkillLevel.Beginner}>{displayNames[SkillLevel.Beginner]}</option>
          <option value={SkillLevel.Advanced}>{displayNames[SkillLevel.Advanced]}</option>
        </FormGroup.Select>
      </div>
    )
  }

  function renderUrlSection() {
    return (
      <div>
        <h4>{t('home.recordedWebinarsSection.admin.addDialog.url')}</h4>
        <FormGroup.Input
          label={t('home.recordedWebinarsSection.admin.addDialog.autoGenerate')}
          onChange={onInputChange}
          name="autoGenerateUrl"
          type="checkbox"
          checked={state.autoGenerateUrl}
        />
        <FormGroup.Input
          onChange={onInputChange}
          name="slug"
          placeholder={t('home.recordedWebinarsSection.admin.addDialog.path')}
          value={slug}
          disabled={state.autoGenerateUrl}
          notBlank
        />
      </div>
    )
  }

  const { error, saving } = apiState
  const errorMessage = error || validationError

  return (
    <Dialog onRequestClose={onRequestClose}>
      <Dialog.Header onRequestClose={onRequestClose}>
        <Dialog.Title>
          {initialRecordedWebinar
            ? t('home.recordedWebinarsSection.admin.addDialog.editTitle')
            : t('home.recordedWebinarsSection.admin.addDialog.title')}
        </Dialog.Title>
      </Dialog.Header>
      <form onSubmit={onSubmit}>
        <ActionDialog.Body>
          {renderSeries()}
          {state.seriesId && (
            <>
              <FormGroup.Input
                onChange={onInputChange}
                name="title"
                placeholder={t('home.recordedWebinarsSection.admin.addDialog.titlePlaceholder')}
                value={title}
                notBlank
              />
              <FormGroup.Textarea
                onChange={onInputChange}
                name="description"
                placeholder={t('home.recordedWebinarsSection.admin.addDialog.descriptionPlaceholder')}
                rows={5}
                value={description}
                required
              />
              {renderSkillLevel()}
              <h4>{t('home.recordedWebinarsSection.admin.addDialog.categories')}</h4>
              <FormGroup.Input
                label={t('productNames.rise')}
                onChange={onInputChange}
                name="rise"
                type="checkbox"
                checked={rise}
              />
              <FormGroup.Input
                label={t('productNames.storyline')}
                onChange={onInputChange}
                name="storyline"
                type="checkbox"
                checked={storyline}
              />
              <FormGroup.Input
                label={t('home.recordedWebinarsSection.admin.addDialog.morePlaceholder')}
                onChange={onInputChange}
                name="more"
                type="checkbox"
                checked={more}
              />
              {renderUrlSection()}
              {initialRecordedWebinar && (
                <>
                  <h4>{t('home.recordedWebinarsSection.admin.addDialog.makePublic')}</h4>
                  <FormGroup.Input
                    label={t('home.recordedWebinarsSection.admin.addDialog.publish')}
                    onChange={onInputChange}
                    name="published"
                    placeholder={t('home.recordedWebinarsSection.admin.addDialog.publish')}
                    type="checkbox"
                    checked={state.published}
                  />
                </>
              )}
            </>
          )}
        </ActionDialog.Body>
        <ActionDialog.Footer>
          {showUrlGenerationWarning && (
            <ActionDialog.Warning>{t('home.recordedWebinarsSection.admin.addDialog.warning')}</ActionDialog.Warning>
          )}
          <div>
            <ActionDialog.CancelButton onClick={onRequestClose} />
            <ActionDialog.SaveButton loading={saving} />
          </div>
          <ActionDialog.Error message={errorMessage} />
        </ActionDialog.Footer>
      </form>
    </Dialog>
  )
}
